import { getUniqueId, reorder } from "@jugl-web/utils";
import { FC } from "react";
import { DragDropContext, DropResult, Droppable } from "react-beautiful-dnd";
import { TaskFieldSidebarListItemLayout } from "../../../TaskFieldSidebarListItemLayout";
import { AddNewValueButton } from "../AddNewValueButton";
import { DropdownValueListItem as DropdownValueListItemComponent } from "./DropdownValueListItem";
import { DropdownValueListItem } from "./types";
import { useDropdownValueList } from "./useDropdownValueList";

interface DropdownValueListProps {
  values: DropdownValueListItem[];
  isReadonly: boolean;
  onValuesChange: (values: DropdownValueListItem[]) => void;
}

export const DropdownValueList: FC<DropdownValueListProps> = ({
  values,
  isReadonly,
  onValuesChange,
}) => {
  const {
    newItem,
    editingItemId,
    handleChangeEditingState,
    handleDismissNewItem,
    handleAddValueClick,
  } = useDropdownValueList();

  const handleAdd = (text: string, color: string) => {
    if (text.trim().length > 0) {
      onValuesChange([...values, { id: getUniqueId(), text, color }]);
    }

    handleDismissNewItem();
  };

  const handleDelete = (id: string) => {
    onValuesChange(values.filter((value) => value.id !== id));
  };

  const handleUpdate = (id: string, text: string, color: string) => {
    if (text.trim().length === 0) {
      handleDelete(id);
      return;
    }

    onValuesChange(
      values.map((value) =>
        value.id === id ? { ...value, text, color } : value
      )
    );
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (
      !destination ||
      source.index === destination.index ||
      values[destination.index]?.isReadonly
    ) {
      return;
    }

    onValuesChange(reorder(values, source.index, destination.index));
  };

  return (
    <div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="customFieldList">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {values.map((value, index) => (
                <TaskFieldSidebarListItemLayout.DraggableContainer
                  key={value.id}
                  id={value.id}
                  index={index}
                  className="mb-4"
                  isDragDisabled={isReadonly || value.isReadonly}
                >
                  <DropdownValueListItemComponent
                    key={value.id}
                    text={value.text}
                    color={value.color}
                    isEditing={editingItemId === value.id}
                    isReadonly={isReadonly || value.isReadonly}
                    onChangeEditingState={(isEditing) =>
                      handleChangeEditingState(value.id, isEditing)
                    }
                    onUpdate={(text, color) =>
                      handleUpdate(value.id, text, color)
                    }
                    onDelete={() => handleDelete(value.id)}
                  />
                </TaskFieldSidebarListItemLayout.DraggableContainer>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {newItem && (
        <DropdownValueListItemComponent
          text={newItem.text}
          color={newItem.color}
          isEditing={editingItemId === newItem.id}
          isReadonly={isReadonly}
          onChangeEditingState={(isEditing) =>
            handleChangeEditingState(newItem.id, isEditing)
          }
          onUpdate={handleAdd}
          onDelete={handleDismissNewItem}
          className="mb-4"
        />
      )}
      {!isReadonly && <AddNewValueButton onClick={handleAddValueClick} />}
    </div>
  );
};
